import { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import ReservationsService from '../../../rest/reservationsService';
import { useReservationUpdates } from './useReservationUpdates';
import MenuService from '../../../rest/menuService';
import ProductService from '../../../rest/productService';
import { extractOrderItems } from '../../../utils/StringUtils';
import { getBrazilDateString } from '../../../utils/DateUtils';

export const useReservationsLogic = ({ mode }) => {
    const [allReservations, setAllReservations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedDate, setSelectedDate] = useState(getBrazilDateString());

    const clientCode = sessionStorage.getItem('clientCode');
    const responsibleId = sessionStorage.getItem('login');

    useReservationUpdates({
        clientCode,
        responsibleId: mode === 'parent' ? responsibleId : null,
        setAllReservations
    });

    const fetchReservations = async (date) => {
        setLoading(true);
        try {
            let data;
            if (mode === 'parent') {
                data = await ReservationsService.listByResponsibleAndDate(clientCode, responsibleId, date);
            } else {
                data = await ReservationsService.listByClientAndDate(clientCode, date);
            }
            setAllReservations(data || []);
        } catch (e) {
            toast.error('Erro ao carregar reservas.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReservations(selectedDate);
    }, [selectedDate]);

    const filteredReservations = useMemo(() => {
        if (statusFilter === 'all') return allReservations;
        return allReservations.filter(r => r.status === statusFilter);
    }, [allReservations, statusFilter]);

    // Group by turnId (then turnName), preserving turn order
    const groupedByTurn = useMemo(() => {
        const groups = {};
        filteredReservations.forEach(r => {
            const key = r.turnId;
            if (!groups[key]) groups[key] = { turnName: r.turnName || `Turno ${r.turnId}`, reservations: [] };
            groups[key].reservations.push(r);
        });
        return Object.entries(groups)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([, group]) => group);
    }, [filteredReservations]);

    // For parent view: group by date, then by turn
    const groupedByDateAndTurn = useMemo(() => {
        if (mode !== 'parent') return null;
        const byDate = {};
        filteredReservations.forEach(r => {
            const dateKey = r.desiredDate;
            if (!byDate[dateKey]) byDate[dateKey] = {};
            const turnKey = r.turnId;
            if (!byDate[dateKey][turnKey]) byDate[dateKey][turnKey] = { turnName: r.turnName || `Turno ${r.turnId}`, reservations: [] };
            byDate[dateKey][turnKey].reservations.push(r);
        });
        return Object.entries(byDate)
            .sort(([a], [b]) => new Date(b) - new Date(a))
            .map(([date, turns]) => ({
                date,
                turns: Object.entries(turns)
                    .sort(([a], [b]) => Number(a) - Number(b))
                    .map(([, t]) => t)
            }));
    }, [filteredReservations, mode]);

    const cancelReservation = async (reservation) => {
        setLoading(true);
        try {
            try {
                const [recipesResp, productsResp] = await Promise.all([
                    MenuService.getAllRecipes({ page: 0, size: 9999 }),
                    ProductService.getAllProducts({ page: 0, size: 9999 })
                ]);
                const recipes = recipesResp.content || [];
                const products = productsResp.content || [];
                const items = extractOrderItems(reservation.itemsList);

                const returnPromises = items
                    .filter(item => item.name && item.quantity > 0)
                    .map(item => {
                        const normalizedName = item.name.trim().toLowerCase();
                        const isRecipe = recipes.some(r => r.name?.toLowerCase() === normalizedName);
                        const isProduct = products.some(p => p.name?.toLowerCase() === normalizedName);
                        const itemType = isRecipe ? 'recipe' : (isProduct ? 'product' : 'combo');

                        return MenuService.returnItems({
                            name: item.name,
                            quantity: item.quantity,
                            type: itemType,
                            client_code: clientCode
                        });
                    });

                await Promise.all(returnPromises);
            } catch (e) {
                console.error("Erro ao devolver itens ao estoque:", e);
            }

            await ReservationsService.cancelReservation(reservation.reservationId);
            setAllReservations(prev => prev.map(r => r.reservationId === reservation.reservationId ? { ...r, status: 'CANCELED' } : r));
            toast.success('Reserva cancelada com sucesso!');
        } catch (e) {
            toast.error('Não foi possível cancelar a reserva.');
        } finally {
            setLoading(false);
        }
    };

    return {
        data: { allReservations, filteredReservations, groupedByTurn, groupedByDateAndTurn, loading, selectedDate, statusFilter },
        actions: {
            setSelectedDate,
            setStatusFilter,
            cancelReservation,
            refresh: () => fetchReservations(selectedDate)
        }
    };
};
